import { DEFAULT_TEMPLATE, type EventStandardDefs } from '@solinkcorp/event-standard';
import * as formatters from '@solinkcorp/event-standard/lib/src/formatters';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import Mustache from 'mustache';
import React, { PureComponent } from 'react';

import { type EventListLayoutType } from '@solinkcorp/authentication';
import { getLanguageCode, type LocaleDefinition } from '@solinkcorp/locale';
import { type IEvent } from 'clients/PlatformApi';
import { type PlayEventHandler } from 'components/EventPlayer/types';
import { type IEventDetailsItem } from 'components/VideoPlayer/components/PlayerOverlay/PlayerOverlay';
import { getReduxStore } from 'stores/store';
import * as EventUtil from 'utils/EventUtil';
import { type CamerasDictionaryCamera } from 'utils/types';
import { EVENT_CARD_FIXED_WIDTH } from 'vd-constants';
import { plainPrintText, PrintFieldRow, PrintText } from './printRows';
import { RendererUtil } from './RendererUtil';
import { type FieldRendererField, type FieldValue } from './types';

/** `{{fieldName}}:<space or tab>{{fieldValue}}<suffix>`, with an optional `-` or `--` prefix. */
const LABEL_VALUE_TEMPLATE =
  /^\s*(-{1,2}\s*)?\{\{fieldName\}\}:\s*\{\{fieldValue\}\}([^{}\s]*)\s*$/;
const UNESCAPED = { escape: (value: unknown) => String(value) };

export interface RenderContentStaticOptions {
  heightOnly?: boolean;
  timezone?: string;
}

export interface FieldRendererProps {
  eventStandard: EventStandardDefs;
  camerasDictionary: { [cameraId: string]: CamerasDictionaryCamera };
  event: IEvent;
  eventId: string;
  fieldMetadata: FieldRendererField;
  fieldName: string;
  fieldValue: FieldValue;
  fontSize?: string;
  locale: LocaleDefinition;
  /** IANA time zone. */
  timezone?: string;
  onContextMenu: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    detailsField?: { name: string; value: FieldValue; i18n: string },
  ) => void;
  onEventRowClick: PlayEventHandler;
  activeItem?: IEventDetailsItem | undefined;
  playerTime?: number;
  isVideoPlaying?: boolean;
  layoutType?: EventListLayoutType;
  /**
   * Print mode. Labels sit in a fixed column and the field renders as plain
   * text instead of the on-screen receipt layout.
   */
  variant?: 'print';
}

export interface StaticRenderer {
  renderContentStatic(
    eventStandard: EventStandardDefs,
    fieldMetadata: FieldRendererField,
    event: IEvent,
    options?: RenderContentStaticOptions,
  ): string[];

  makeTemplateContextStatic(
    eventStandard: EventStandardDefs,
    fieldName: string,
    fieldValue: FieldValue | FieldValue[],
    fieldMetadata: FieldRendererField,
    options?: { timezone?: string },
  ): { fieldName: string; fieldValue: string | string[] };
}

/**
 * Base class for field renderers.
 * Extend this class for field or field type specific rendering.
 * Override the `getTemplate()` or `makeTemplateContext()` to customize the template or its context.
 *  Default behavior grabs the template from field metadata, and uses the `{fieldName, fieldValue}` context.
 * For capsule-based search highlight to work on non-details field, override the `getCapsuleName` function.
 * Finally, add the new renderer class to `GenericFieldRenderers` (associating it to a field type)
 *  or `SpecificFieldRenderer` (associating it to a field id)
 */
export class BaseFieldRenderer extends PureComponent<FieldRendererProps> {
  protected node: HTMLDivElement | undefined;

  render(): JSX.Element | null {
    if (this.props.variant === 'print') {
      return this.renderPrint();
    }

    return (
      <div
        className="receipt"
        data-capsule-name={this.getCapsuleName()}
        data-testid={this.getCapsuleTestId()}
        onContextMenu={this.onContextMenu}
        style={this.style()}
        ref={(node) => {
          this.node = node || undefined;
        }}
      >
        <div className="pre" dangerouslySetInnerHTML={{ __html: this.renderContent() }} />
      </div>
    );
  }

  /** Print uses the formatted field text. Padding from the receipt template is removed. */
  renderPrint(): JSX.Element | null {
    const { eventStandard, fieldName, fieldValue, fieldMetadata, timezone } = this.props;
    const template = (this.constructor as typeof BaseFieldRenderer).getTemplate(fieldMetadata);
    const rowProps = {
      'data-capsule-name': this.getCapsuleName(),
      'data-testid': this.getCapsuleTestId(),
      onContextMenu: this.onContextMenu,
    };

    if (template.includes('{{#fieldValue}}')) {
      const rendered = plainPrintText(
        Mustache.render(template, { fieldName: this.i18nFieldName(), fieldValue }, {}, UNESCAPED),
      );
      return rendered ? <PrintText {...rowProps}>{rendered}</PrintText> : null;
    }

    const context = this.getStaticRenderer().makeTemplateContextStatic(
      eventStandard,
      fieldName,
      fieldValue,
      fieldMetadata,
      { timezone },
    );
    if (!context) {
      return null;
    }

    const value = (
      Array.isArray(context.fieldValue) ? context.fieldValue.join(', ') : context.fieldValue
    ).trim();
    if (!value || value.includes('[object Object]')) {
      return null;
    }

    const labelValue = LABEL_VALUE_TEMPLATE.exec(template);
    if (labelValue) {
      return (
        <PrintFieldRow {...rowProps} label={context.fieldName} value={`${value}${labelValue[2]}`} />
      );
    }

    const rendered = plainPrintText(Mustache.render(template, context, {}, UNESCAPED));
    return rendered ? <PrintText {...rowProps}>{rendered}</PrintText> : null;
  }

  getCapsuleName(): string | undefined {
    const { fieldMetadata } = this.props;

    const prefix = 'details.';
    if (fieldMetadata.name.startsWith(prefix)) {
      return fieldMetadata.name.substring(prefix.length);
    }
  }

  /** QA test id for this capsule, e.g. `zoneLabel` → `event-card-capsule-zone-label`. */
  getCapsuleTestId(): string | undefined {
    const capsuleName = this.getCapsuleName();
    if (!capsuleName) {
      return undefined;
    }
    return `event-card-capsule-${kebabCase(capsuleName)}`;
  }

  renderContent(): string {
    const { eventStandard, event, fieldMetadata, timezone } = this.props;

    return this.getStaticRenderer()
      .renderContentStatic(eventStandard, fieldMetadata, event, {
        timezone,
      })
      .join('');
  }

  getStaticRenderer(): StaticRenderer {
    return this.constructor as any as StaticRenderer;
  }

  static renderContentStatic(
    eventStandard: EventStandardDefs,
    fieldMetadata: FieldRendererField,
    event: IEvent,
    options?: RenderContentStaticOptions,
  ): string[] {
    const fieldName = fieldMetadata.name;
    const fieldValue = get(event, fieldMetadata.name);
    try {
      return [
        Mustache.render(
          this.getTemplate(fieldMetadata),
          this.makeTemplateContextStatic(
            eventStandard,
            fieldName,
            fieldValue,
            fieldMetadata,
            options,
          ),
        ),
      ];
    } catch (error) {
      console.error(error);
      return [''];
    }
  }

  static getTemplate(fieldMetadata: FieldRendererField): string {
    return fieldMetadata.template || DEFAULT_TEMPLATE;
  }

  // makeTemplateContext() {
  //   const { eventStandard, fieldName, fieldValue, fieldMetadata, timezone } = this.props;
  //   return this.getStaticRenderer().makeTemplateContextStatic(
  //     eventStandard,
  //     fieldName,
  //     fieldValue,
  //     fieldMetadata,
  //     {
  //       timezone,
  //     },
  //   );
  // }

  static makeTemplateContextStatic(
    eventStandard: EventStandardDefs,
    fieldName: string,
    fieldValue: FieldValue | FieldValue[],
    fieldMetadata: FieldRendererField,
    options?: { timezone?: string },
  ): { fieldName: string; fieldValue: string | string[] } | undefined {
    if (Array.isArray(fieldValue)) {
      if (!fieldValue.length) return;

      const { options: formatterOptions } = formatters.getFormatter(fieldMetadata.formatter);

      return {
        fieldName: EventUtil.i18nEventFieldName(fieldName, false, eventStandard),
        fieldValue: formatterOptions?.applyToCollection
          ? this.formatFieldValue(fieldValue, fieldMetadata, options)
          : fieldValue.map((v) => this.formatFieldValue(v, fieldMetadata, options)).join(', '),
      };
    }
    return {
      fieldName: EventUtil.i18nEventFieldName(fieldName, false, eventStandard),
      fieldValue: this.formatFieldValue(fieldValue, fieldMetadata, options),
    };
  }

  onContextMenu = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    const { fieldMetadata, fieldValue, onContextMenu } = this.props;
    const prefix = 'details.'; // capsule search only works on details fields at the moment
    if (fieldMetadata.name.startsWith(prefix)) {
      const name = fieldMetadata.name.substring(prefix.length);
      onContextMenu(event, { name, value: fieldValue, i18n: this.i18nFieldName() });
    } else {
      onContextMenu(event);
    }
  };

  style(): React.CSSProperties {
    const { fontSize } = this.props;
    const style: React.CSSProperties = {};
    if (fontSize) {
      style['fontSize'] = fontSize;
    }
    return style;
  }

  i18nFieldName(): string {
    const { eventStandard, fieldName } = this.props;
    return EventUtil.i18nEventFieldName(fieldName, false, eventStandard);
  }

  static formatFieldValue(
    fieldValue: FieldValue,
    fieldMetadata: FieldRendererField,
    { timezone }: { timezone?: string } = {},
  ): string {
    // TODO: https://github.com/SolinkCorp/event-standard/pull/297
    const { formatter, options } = formatters.getFormatter(fieldMetadata.formatter);

    const metadata: { timezone?: string; locale?: string } = { timezone };
    if (options && options.requiresLocale) {
      metadata.locale = getLanguageCode(getReduxStore().getState().localeState.localeCode);
    }

    if (
      fieldValue === null ||
      fieldValue === undefined ||
      (typeof fieldValue !== 'string' &&
        typeof fieldValue !== 'number' &&
        typeof fieldValue !== 'object')
    ) {
      return '';
    }

    return formatter(fieldValue, metadata);
  }
}

export class FixedWidthFieldRenderer extends BaseFieldRenderer {
  renderContent() {
    const { eventStandard, event, fieldMetadata, timezone } = this.props;

    return `<div class="pre">${this.getStaticRenderer()
      .renderContentStatic(eventStandard, fieldMetadata, event, { timezone })
      .join('</div><div class="pre">')}</div>`;
  }

  static renderContentStatic(
    eventStandard: EventStandardDefs,
    fieldMetadata: FieldRendererField,
    event: IEvent,
    options?: RenderContentStaticOptions,
  ): string[] {
    const fieldName = fieldMetadata.name;
    const fieldValue = get(event, fieldName, undefined) as any;

    if (
      fieldValue === undefined ||
      fieldValue === null ||
      (Array.isArray(fieldValue) && fieldValue.length === 0)
    ) {
      return [''];
    }

    try {
      const rendered = Mustache.render(
        this.getTemplate(fieldMetadata),
        this.makeTemplateContextStatic(
          eventStandard,
          fieldName,
          fieldValue,
          fieldMetadata,
          options,
        ),
      );
      return rendered
        .split('\n')
        .map((line) =>
          RendererUtil.makeLineFixedWidth(EVENT_CARD_FIXED_WIDTH, line, { force: true }),
        )
        .reduce((acc, lines) => acc.concat(lines), []);
    } catch (error) {
      console.error(error);
      return [''];
    }
  }
}
